import { useState, useEffect } from 'react';
import { Eye, Globe, Smartphone, Tablet, Monitor, ExternalLink } from 'lucide-react';
import socketService from '@/services/socket';

const LiveNotifications = ({ userId, showToasts = true, maxNotifications = 5 }) => {
    const [notifications, setNotifications] = useState([]);
    const [todayViews, setTodayViews] = useState(0);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        // Use existing socket connection (don't create new one)
        const socketConnected = socketService.isSocketConnected();

        // Set initial connection state
        setIsConnected(socketConnected);

        // Request analytics if already connected
        if (socketConnected) {
            socketService.requestLiveAnalytics();
        }

        // Backup polling mechanism for PM2 cluster compatibility
        const pollInterval = setInterval(() => {
            if (socketService.isSocketConnected()) {
                socketService.requestLiveAnalytics();
            }
        }, 5000); // Poll every 5 seconds for updates

        // Socket event handlers
        const handleSocketReady = () => {
            setIsConnected(true);
            socketService.requestLiveAnalytics();
        };

        const handleSocketDisconnected = () => {
            setIsConnected(false);
        };

        const handleCardViewed = (data) => {
            // Add new notification
            const notification = {
                id: Date.now(),
                type: 'view',
                ...data,
                timestamp: new Date(data.timestamp)
            };

            setNotifications(prev => [notification, ...prev].slice(0, maxNotifications));
            setTodayViews(prev => prev + 1);

            // Show toast notification if enabled
            if (showToasts) {
                showToastNotification(notification);
            }
        };

        const handleLiveAnalytics = (data) => {
            const newTodayViews = data.todayViews;

            // Check if view count increased (indicates new views)
            if (newTodayViews > todayViews) {
                // Create synthetic notification for new views detected via polling
                if (todayViews > 0) { // Only if we had a previous count (not initial load)
                    const syntheticNotification = {
                        id: Date.now(),
                        type: 'view',
                        uuid: 'detected-via-polling',
                        userId: userId,
                        timestamp: new Date(),
                        deviceType: 'unknown',
                        referrer: { type: 'Detected via polling', source: 'Analytics' },
                        location: { ip: 'unknown' },
                        userAgent: 'Polling detection'
                    };

                    setNotifications(prev => [syntheticNotification, ...prev].slice(0, maxNotifications));

                    if (showToasts) {
                        showToastNotification(syntheticNotification);
                    }
                }
            }

            setTodayViews(newTodayViews);
        };

        // Register event listeners
        socketService.on('socketReady', handleSocketReady);
        socketService.on('socketDisconnected', handleSocketDisconnected);
        socketService.on('cardViewed', handleCardViewed);
        socketService.on('liveAnalytics', handleLiveAnalytics);

        // Cleanup on unmount
        return () => {
            clearInterval(pollInterval);
            socketService.off('socketReady', handleSocketReady);
            socketService.off('socketDisconnected', handleSocketDisconnected);
            socketService.off('cardViewed', handleCardViewed);
            socketService.off('liveAnalytics', handleLiveAnalytics);
        };
    }, [userId, maxNotifications, showToasts]);

    const showToastNotification = (notification) => {
        // Create a toast notification
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('New Card View!', {
                body: `Someone viewed your card from ${getDeviceIcon(notification.deviceType)} ${notification.deviceType}`,
                icon: '/favicon-32x32.png',
                tag: 'card-view'
            });
        }
    };

    const getDeviceIcon = (deviceType) => {
        switch (deviceType) {
            case 'mobile': return Smartphone;
            case 'tablet': return Tablet;
            case 'desktop': return Monitor;
            default: return Globe;
        }
    };

    const getTimeAgo = (timestamp) => {
        const now = new Date();
        const diff = now - timestamp;
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor(diff / 1000);

        if (minutes > 0) return `${minutes}m ago`;
        return `${seconds}s ago`;
    };

    const formatReferrer = (referrer) => {
        if (!referrer) return 'Direct';

        // Handle object format from backend {type: 'Direct', source: 'Direct'}
        if (typeof referrer === 'object') {
            if (referrer.type === 'Direct') return 'Direct';
            if (referrer.type === 'QR') return 'QR Code';
            if (referrer.type === 'Email') return 'Email';
            if (referrer.type === 'Social') return 'Social Media';
            if (referrer.type === 'Test') return 'Test';
            return referrer.type || 'External Link';
        }

        // Handle string format (legacy)
        if (referrer === 'Direct') return 'Direct';
        if (typeof referrer === 'string') {
            if (referrer.includes('qr')) return 'QR Code';
            if (referrer.includes('email')) return 'Email';
            if (referrer.includes('social')) return 'Social Media';
            return 'External Link';
        }

        return 'External Link';
    };

    return (
        <div className="space-y-4">
            {/* Connection Status */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-600">
                        {isConnected ? 'Live' : 'Offline'}
                    </span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                    {todayViews} views today
                </div>
            </div>

            {/* Live Notifications */}
            {notifications.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-900 flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        Recent Views
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {notifications.map((notification) => {
                            const DeviceIcon = getDeviceIcon(notification.deviceType);
                            return (
                                <div
                                    key={notification.id}
                                    className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg transition-all duration-300 hover:bg-blue-100"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                            <DeviceIcon className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900">
                                                New {notification.deviceType} view
                                            </p>
                                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                                                <span>{formatReferrer(notification.referrer)}</span>
                                                <span>•</span>
                                                <span>{notification.location?.ip}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0 text-xs text-gray-500">
                                        {getTimeAgo(notification.timestamp)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {notifications.length === 0 && isConnected && (
                <div className="text-center py-8 text-gray-500">
                    <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Waiting for card views...</p>
                    <p className="text-xs text-gray-400 mt-1">
                        Real-time notifications will appear here
                    </p>
                </div>
            )}
        </div>
    );
};

export default LiveNotifications;